import { ReactNode, AriaAttributes, useState, useRef, useId, useEffect, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { focusNext } from "./accessibility";

interface DropdownMenuContextValue {
    closeMenu: () => void;
    isControlled: boolean;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null);

interface DropdownItemProps {
    onClick?: () => void;
    children: ReactNode;
}

export const DropdownItem = ({ onClick, children }: DropdownItemProps) => {
    const context = useContext(DropdownMenuContext);
    if (!context) throw new Error(`DropdownItem must be used within DropdownMenu`);

    const handleClick = () => {
        onClick?.();
        // Auto-close menu after click if uncontrolled
        if (!context.isControlled) {
            context.closeMenu();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === `Enter` || e.key === ` `) {
            e.preventDefault();
            handleClick();
        }
    };

    return (
        <div
            role="menuitem"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className="w-full p-2 gap-2 rounded cursor-pointer hover:bg-neutral-50 focus:outline-none focus:bg-neutral-50 focus-visible:outline-4 focus-visible:outline-blue-400 focus-visible:bg-neutral-50 text-base leading-6 text-neutral-700 shrink-0 truncate"
        >
            {children}
        </div>
    );
};

export type DropdownMenuTriggerProps = {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    [`aria-expanded`]: AriaAttributes[`aria-expanded`];
    [`aria-haspopup`]: AriaAttributes[`aria-haspopup`];
    [`aria-controls`]: AriaAttributes[`aria-controls`];
};

type TriggerProp = (props: DropdownMenuTriggerProps) => ReactNode;

interface DropdownMenuProps {
    children: ReactNode;
    trigger?: TriggerProp;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export const DropdownMenu = ({ children, trigger, open, onOpenChange }: DropdownMenuProps) => {
    const isControlled = open !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const [shouldRender, setShouldRender] = useState(false);
    const triggerRef = useRef<HTMLElement | null>(null);
    const menuId = useId();

    const isOpen = isControlled ? open : internalOpen;
    const pendingFrameHandle = useRef<number | null>(null);
    const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setIsOpen = (value: boolean) => {
        // Cancel any in-flight async work so rapid successive opens/closes
        // don't clobber state
        if (pendingFrameHandle.current !== null) {
            cancelAnimationFrame(pendingFrameHandle.current);
            pendingFrameHandle.current = null;
        }
        if (pendingTimeout.current !== null) {
            clearTimeout(pendingTimeout.current);
            pendingTimeout.current = null;
        }

        if (value) {
            // First render the element (with opacity 0)
            setShouldRender(true);
            // Wait for next frame before triggering the fade in
            pendingFrameHandle.current = requestAnimationFrame(() => {
                pendingFrameHandle.current = null;
                if (isControlled) {
                    onOpenChange?.(value);
                } else {
                    setInternalOpen(value);
                }
            });
        } else {
            // Immediately hide (fade out)
            if (isControlled) {
                onOpenChange?.(value);
            } else {
                setInternalOpen(value);
            }
            // Delay unmounting to allow fade out animation
            pendingTimeout.current = setTimeout(() => {
                pendingTimeout.current = null;
                setShouldRender(false);
            }, 200);
        }
    };

    // Configure Popper
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: `bottom`,
        modifiers: [
            {
                name: `offset`,
                options: {
                    offset: [0, 4]
                }
            },
            {
                name: `preventOverflow`,
                options: {
                    padding: 8
                }
            },
            {
                name: `flip`,
                options: {
                    fallbackPlacements: [`top`, `bottom`, `left`, `right`]
                }
            }
        ]
    });

    // Handle clicks outside both trigger and menu
    useEffect(() => {
        if (!trigger || !isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedOutsideTrigger = referenceElement && !referenceElement.contains(target);
            const clickedOutsideMenu = popperElement && !popperElement.contains(target);

            if (clickedOutsideTrigger && clickedOutsideMenu) {
                setIsOpen(false);
            }
        };

        document.addEventListener(`mousedown`, handleClickOutside);
        return () => {
            document.removeEventListener(`mousedown`, handleClickOutside);
        };
    }, [trigger, isOpen, referenceElement, popperElement]);

    const menuRefCallback = (node: HTMLDivElement | null) => {
        setPopperElement(node);
        if (node && isOpen) {
            // Focus first menu item when menu mounts
            const firstItem = node.querySelector(`[role="menuitem"]`) as HTMLElement;
            // preventScroll should prevent the page from scrolling to the top
            firstItem?.focus({ preventScroll: true });
        }
    };

    const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
        triggerRef.current = event.currentTarget;
        setReferenceElement(event.currentTarget);
        setIsOpen(!isOpen);
    };

    const closeMenu = () => {
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const menuElement = event.currentTarget;
        const menuItems = Array.from(
            menuElement.querySelectorAll(`[role="menuitem"]`)
        ) as HTMLElement[];
        const currentIndex = menuItems.indexOf(document.activeElement as HTMLElement);

        switch (event.key) {
            case `ArrowDown`: {
                event.preventDefault();
                const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
                menuItems[nextIndex]?.focus();
                break;
            }
            case `ArrowUp`: {
                event.preventDefault();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
                menuItems[prevIndex]?.focus();
                break;
            }
            case `Home`:
                event.preventDefault();
                menuItems[0]?.focus();
                break;
            case `End`:
                event.preventDefault();
                menuItems[menuItems.length - 1]?.focus();
                break;
            case `Tab`:
                event.preventDefault();
                setIsOpen(false);
                if (triggerRef.current) {
                    focusNext(triggerRef.current, event.shiftKey);
                }
                break;
            case `Escape`:
                event.preventDefault();
                closeMenu();
                break;
        }
    };

    const contextValue: DropdownMenuContextValue = {
        closeMenu,
        isControlled
    };

    if (!trigger) {
        return (
            <DropdownMenuContext.Provider value={contextValue}>
                <div
                    className="flex w-fit max-w-[232px] max-h-[248px] overflow-y-auto p-2 flex-col items-start gap-2 rounded-lg border border-neutral-300 bg-white z-[100]"
                    style={{ boxShadow: `0 2px 4px 0 rgba(34, 41, 51, 0.24)` }}
                    role="menu"
                >
                    {children}
                </div>
            </DropdownMenuContext.Provider>
        );
    }

    const triggerElement = trigger({
        onClick: handleTriggerClick,
        'aria-expanded': isOpen,
        'aria-haspopup': `menu`,
        'aria-controls': menuId
    });

    return (
        <DropdownMenuContext.Provider value={contextValue}>
            {triggerElement}
            {shouldRender && createPortal(
                <div
                    ref={menuRefCallback}
                    style={{
                        ...styles.popper,
                        zIndex: 1000,
                        opacity: isOpen ? 1 : 0,
                        transition: `opacity 200ms ease-in-out`
                    }}
                    {...attributes.popper}
                >
                    <div
                        role="menu"
                        id={menuId}
                        onKeyDown={handleMenuKeyDown}
                        className="flex w-fit max-w-[232px] max-h-[248px] overflow-y-auto p-2 flex-col items-start gap-2 rounded-lg border border-neutral-300 bg-white"
                        style={{ boxShadow: `0 2px 4px 0 rgba(34, 41, 51, 0.24)` }}
                    >
                        {children}
                    </div>
                </div>,
                document.body
            )}
        </DropdownMenuContext.Provider>
    );
};
